import React from "react";

const SERIES_CONFIG = {
    low: { label: "LOW", color: "#FFD147" },
    medium: { label: "MEDIUM", color: "#F58A00" },
    high: { label: "HIGH", color: "#E84E2C" },
    mandatory: { label: "MANDATORY", color: "#946EB1" },
};

const SERIES_ORDER = ["low", "medium", "high", "mandatory"];

const getNiceStep = (rawStep) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalized = rawStep / magnitude;
    if (normalized <= 1) return 1 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
};

/** Same Y-axis rounding as Loss Scenarios (USDM) / MFL Business Interruption horizontal scales. */
const buildAxisScale = (maxValue, tickCount = 6) => {
    const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 0;
    const rawStep = safeMax / Math.max(1, tickCount - 1);
    const step = getNiceStep(rawStep || 1);
    const axisMax = Math.max(step, Math.ceil(safeMax / step) * step);
    const ticks = [];
    for (let value = 0; value <= axisMax; value += step) {
        ticks.push(value);
    }
    if (ticks[ticks.length - 1] !== axisMax) {
        ticks.push(axisMax);
    }
    return { axisMax, ticks };
};

export default function RecommendationPriorityChart({ rows = [] }) {
    const [visibleSeries, setVisibleSeries] = React.useState({
        low: true,
        medium: true,
        high: true,
        mandatory: true,
    });

    const toNumber = (value) => {
        if (value === null || value === undefined) return 0;
        const num = Number(String(value).trim());
        return Number.isNaN(num) ? 0 : num;
    };

    const baseRows = React.useMemo(
        () =>
            (rows || []).map((row) => ({
                label: row.label ?? "",
                low: toNumber(row.low),
                medium: toNumber(row.medium),
                high: toNumber(row.high),
                mandatory: toNumber(row.mandatory),
            })),
        [rows]
    );

    const displayRows = React.useMemo(
        () =>
            baseRows.map((r) => ({
                ...r,
                low: visibleSeries.low ? r.low : 0,
                medium: visibleSeries.medium ? r.medium : 0,
                high: visibleSeries.high ? r.high : 0,
                mandatory: visibleSeries.mandatory ? r.mandatory : 0,
            })),
        [baseRows, visibleSeries]
    );

    const totals = displayRows.map(
        (r) => r.low + r.medium + r.high + r.mandatory
    );

    const maxTotalFromData = totals.length > 0 ? Math.max(...totals) : 0;
    const scaleInput =
        totals.length > 0 ? Math.max(10, maxTotalFromData) : 10;

    const { axisMax, ticks } = React.useMemo(
        () => buildAxisScale(scaleInput, 6),
        [scaleInput]
    );

    const toggleSeries = (key) => {
        setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const chartHeightPx = 280;

    return (
        <div className="w-full overflow-x-auto">
            <div className="min-w-[900px] rounded bg-white p-6">
                {/* Legend — top row, right-aligned (reference layout) */}
                <div className="mb-4 flex flex-wrap items-center justify-end gap-x-6 gap-y-2">
                    {SERIES_ORDER.map((seriesKey) => (
                        <button
                            key={seriesKey}
                            type="button"
                            onClick={() => toggleSeries(seriesKey)}
                            className={`flex items-center gap-2 text-sm font-bold text-gray-900 transition-opacity ${visibleSeries[seriesKey]
                                ? "opacity-100"
                                : "opacity-40"
                                }`}
                        >
                            <span
                                className="h-4 w-8 shrink-0 rounded-full"
                                style={{
                                    backgroundColor:
                                        SERIES_CONFIG[seriesKey].color,
                                }}
                            />
                            <span>{SERIES_CONFIG[seriesKey].label}</span>
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-[48px_minmax(0,1fr)] gap-x-4">
                    {/* Y-axis */}
                    <div
                        className="flex flex-col justify-between text-right text-xs font-medium text-gray-900"
                        style={{ height: chartHeightPx }}
                    >
                        {[...ticks].reverse().map((t) => (
                            <span key={t}>{t}</span>
                        ))}
                    </div>

                    {/* Stacked bars + labels: one column per category so X labels align with bars */}
                    <div className="flex gap-2 px-1">
                        {displayRows.map((row, colIndex) => {
                            const total = totals[colIndex] ?? 0;
                            const barHeightPx =
                                axisMax > 0 && total > 0
                                    ? (total / axisMax) * chartHeightPx
                                    : 0;

                            const topKey = [...SERIES_ORDER]
                                .reverse()
                                .find((k) => row[k] > 0);

                            return (
                                <div
                                    key={`${row.label}-${colIndex}`}
                                    className="flex min-w-0 flex-1 flex-col items-center"
                                >
                                    <div
                                        className="flex w-full items-end justify-center"
                                        style={{ height: chartHeightPx }}
                                    >
                                        {total > 0 ? (
                                            <div
                                                className="flex w-full min-w-[40px] max-w-[100px] flex-col-reverse gap-px overflow-hidden rounded-t-lg bg-white"
                                                style={{
                                                    height: barHeightPx,
                                                }}
                                            >
                                                {SERIES_ORDER.map((key) => {
                                                    const val = row[key];
                                                    if (val <= 0) return null;
                                                    return (
                                                        <div
                                                            key={key}
                                                            className={`w-full min-h-[2px] ${key === topKey
                                                                ? "rounded-t-md"
                                                                : ""
                                                                }`}
                                                            style={{
                                                                flexGrow: val,
                                                                flexBasis: 0,
                                                                backgroundColor:
                                                                    SERIES_CONFIG[
                                                                        key
                                                                    ].color,
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div
                                                className="w-full min-w-[40px] max-w-[100px]"
                                                style={{ height: 0 }}
                                            />
                                        )}
                                    </div>
                                    <div className="mt-3 w-full max-w-[100px] text-center text-[10px] font-bold uppercase leading-tight text-gray-900">
                                        {row.label}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
